import { readonly, ref, type DeepReadonly, type Ref } from 'vue';

export type ToastTone = 'success' | 'error' | 'info';

export interface Toast {
  readonly id: number;
  readonly tone: ToastTone;
  readonly message: string;
}

const DEFAULT_TIMEOUT_MS = 4500;

const items = ref<Toast[]>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();
let nextId = 0;

function dismiss(id: number): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
  items.value = items.value.filter((toast) => toast.id !== id);
}

function push(tone: ToastTone, message: string, timeoutMs = DEFAULT_TIMEOUT_MS): number {
  nextId += 1;
  const id = nextId;
  items.value = [...items.value, { id, tone, message }];
  if (timeoutMs > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), timeoutMs),
    );
  }
  return id;
}

export interface ToastApi {
  readonly toasts: DeepReadonly<Ref<Toast[]>>;
  success(message: string): number;
  error(message: string): number;
  info(message: string): number;
  dismiss(id: number): void;
  clear(): void;
}

export function useToast(): ToastApi {
  return {
    toasts: readonly(items),
    success: (message: string) => push('success', message),
    error: (message: string) => push('error', message, 7000),
    info: (message: string) => push('info', message),
    dismiss,
    clear: () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
      items.value = [];
    },
  };
}
